import { Formik, type FormikHelpers } from 'formik';
import { useCallback, useState } from 'react';
import * as Yup from 'yup';
import Button from '../../components/common/Buttons/Button/Button';
import Card from '../../components/common/Card/Card';
import CardActions from '../../components/common/Card/CardActions';
import FormFieldError from '../../components/common/Form/Errors/FormFieldError';
import Input from '../../components/common/Form/Input/Input';
import ListBox from '../../components/common/Form/ListBox/ListBox';
import Switch from '../../components/common/Form/Switch/Switch';
import ContainerSpinner from '../../components/common/Spinner/ContainerSpinner/ContainerSpinner';
import Typography from '../../components/common/Typography/Typography';
import Loading from '../../components/Loading/Loading';
import ProfileTabs from '../../components/Profile/ProfileTabs/ProfileTabs';
import { useToastUpdater } from '../../context/toastContext/toastContext';
import AppLayout from '../../layout/AppLayout/AppLayout';
import { useAuthUpdater } from '../../lib/auth/context/authContext';
import useAuthSession from '../../lib/auth/hooks/useAuthSession';
import userService from '../../services/user.service';
import type { HubConfig } from '../../types/API/user/User';

export const dateOrderPreference = [
    { id: 1, value: 'asc', label: 'Date Ascending' },
    { id: 2, value: 'dsc', label: 'Date Descending' },
];

const updateProfileFormSchema = Yup.object().shape({
    firstname: Yup.string().required('First name is required'),
    lastname: Yup.string().required('Last name is required'),
    phone: Yup.string(),
    table_filters: Yup.boolean(),
    date_asc_desc: Yup.object()
        .shape({
            id: Yup.number(),
            value: Yup.string().oneOf(['asc', 'dsc']),
            label: Yup.string(),
        })
        .required('Table ordering is required'),
});

const Profile = () => {
    const { session } = useAuthSession({ required: true });
    const { refreshSession } = useAuthUpdater();
    const { user } = session!;
    const [apiErrors, setApiErrors] = useState<string[]>([]);

    const { addToast } = useToastUpdater();

    const initialValues = {
        firstname: user?.firstname || '',
        lastname: user?.lastname || '',
        phone: user?.phone || '',
        table_filters: user.config.hub?.table_filters || false,
        date_asc_desc:
            dateOrderPreference.find(({ value }) => value === user.config.hub?.date_asc_desc) || dateOrderPreference[0],
    };

    type Values = typeof initialValues;

    const handleSubmit = useCallback(
        async (values: Values, { setSubmitting, resetForm }: FormikHelpers<Values>) => {
            setSubmitting(true);

            const updateRequests = await Promise.allSettled([
                userService.updateProfile({
                    firstname: values.firstname,
                    lastname: values.lastname,
                    phone: values.phone,
                }),
                userService.updateUserConfig({
                    ...user.config.hub,
                    table_filters: values.table_filters,
                    date_asc_desc: values.date_asc_desc.value as Required<HubConfig>['date_asc_desc'],
                }),
            ]);

            await refreshSession();

            updateRequests.forEach((request, index) => {
                if (request.status === 'fulfilled') {
                    const message =
                        index === 0
                            ? 'Your personal details have been updated'
                            : 'Your profile preferences have been updated';

                    addToast(message, 'success', '');
                } else {
                    let message =
                        index === 0
                            ? 'Your personal details could not be updated.'
                            : 'Your profile preferences could not be updated.';

                    message += request.reason.response?.message ? ` ${request.reason.response?.message}` : '';

                    if (!message.endsWith('.')) {
                        message += '.';
                    }

                    const apiErrors = request.reason.response?.errors;
                    if (apiErrors) {
                        const apiErrorArray = Object.keys(apiErrors).reduce((acc, key) => {
                            if (apiErrors[key] && apiErrors[key].length) {
                                return [...acc, ...apiErrors[key]];
                            }

                            return acc;
                        }, [] as string[]);
                        setApiErrors(apiErrorArray);
                    }

                    addToast(message, 'error', '');
                }
            });

            resetForm({ values });
            setSubmitting(false);
            // We've purposely disabled this as we don't need addToast or refreshSession to be in the
            // dependency changes.
        },
        [user?.id], // eslint-disable-line react-hooks/exhaustive-deps
    );
    return (
        <AppLayout appendTitle="Viewing profile" tour="profile-settings">
            <div className="md:flex min-w-full">
                <div className="flex items-center m-none md:items-baseline md:grow-0 md:shrink-0 justify-between">
                    <ProfileTabs />
                </div>
                <Card padding="large" className="flex-grow space-y-[2.25rem]">
                    <Typography as="h3" variant="subTitle">
                        Your Profile
                    </Typography>
                    <Loading isLoading={!user} loadingFallback={<ContainerSpinner />}>
                        <Formik
                            validationSchema={updateProfileFormSchema}
                            initialValues={initialValues}
                            onSubmit={handleSubmit}
                        >
                            {({ errors, values, touched, handleSubmit, isSubmitting, handleChange, handleBlur }) => (
                                <form className="space-y-2" onSubmit={handleSubmit}>
                                    <fieldset className="space-y-1.5">
                                        <Typography as="legend" variant="subHeading">
                                            Personal details
                                        </Typography>
                                        <div className="flex flex-col gap-1.5 type-meta md:flex-row">
                                            <div className="space-y-0.25">
                                                <Input
                                                    id="profile-first-name"
                                                    label="First name"
                                                    placeholder="value"
                                                    name="firstname"
                                                    value={values.firstname}
                                                    error={touched.firstname && !!errors.firstname}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                {touched.firstname && errors.firstname ? (
                                                    <FormFieldError>{errors.firstname as string}</FormFieldError>
                                                ) : null}
                                            </div>
                                            <div className="space-y-0.25">
                                                <Input
                                                    id="profile-first-name"
                                                    label="Last name"
                                                    placeholder="value"
                                                    name="lastname"
                                                    value={values.lastname}
                                                    error={touched.lastname && !!errors.lastname}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                {touched.lastname && errors.lastname ? (
                                                    <FormFieldError>{errors.lastname as string}</FormFieldError>
                                                ) : null}
                                            </div>
                                        </div>
                                    </fieldset>
                                    <fieldset className="space-y-1.5">
                                        <Typography as="legend" variant="subHeading">
                                            Contact details
                                        </Typography>
                                        <div className="flex flex-col gap-1.5 type-meta md:flex-row">
                                            <div>
                                                <Input
                                                    id="profile-name"
                                                    label="User name"
                                                    placeholder="User name"
                                                    name="name"
                                                    value={`${user?.firstname} ${user?.lastname}`}
                                                    disabled
                                                />
                                            </div>
                                            <div className="space-y-0.25">
                                                <Input
                                                    id="profile-phone-number"
                                                    label="Telephone"
                                                    placeholder="value"
                                                    name="phone"
                                                    value={values.phone}
                                                    error={touched.phone && !!errors.phone}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                />
                                                {touched.phone && errors.phone ? (
                                                    <FormFieldError>{errors.phone as string}</FormFieldError>
                                                ) : null}
                                            </div>
                                        </div>
                                    </fieldset>
                                    <fieldset className="space-y-1.5">
                                        <Typography as="legend" variant="subHeading">
                                            User Preferences
                                        </Typography>
                                        <div className="flex flex-col gap-1.5 type-meta md:flex-row">
                                            {/* keep search across page yes/no */}
                                            <div className="space-y-0.25">
                                                <Typography as="p" variant="meta" color="pine">
                                                    Keep table filters across page
                                                </Typography>
                                                <Switch
                                                    label="Keep search across page"
                                                    onChange={(_isOn, event) => handleChange(event)}
                                                    name="table_filters"
                                                    isOn={values.table_filters}
                                                />
                                                {touched.table_filters && errors.table_filters ? (
                                                    <FormFieldError>{errors.table_filters as string}</FormFieldError>
                                                ) : null}
                                            </div>
                                        </div>

                                        {/* table ordering desc/asc */}
                                        <div className="space-y-0.25 max-w-[11.25rem] w-full">
                                            <ListBox
                                                label="Table ordering"
                                                name="date_asc_desc"
                                                onSelect={(_selectedItem, event) => {
                                                    handleChange(event);
                                                }}
                                                selectedItem={values.date_asc_desc}
                                                error={touched.date_asc_desc && !!errors.date_asc_desc}
                                                items={dateOrderPreference}
                                            />
                                            {touched.date_asc_desc && errors.date_asc_desc ? (
                                                <FormFieldError>{errors.date_asc_desc as string}</FormFieldError>
                                            ) : null}
                                        </div>
                                    </fieldset>
                                    {apiErrors.length ? (
                                        <ul>
                                            {apiErrors.map((error) => (
                                                <li key={error}>
                                                    <FormFieldError>{error}</FormFieldError>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                    <CardActions>
                                        <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
                                            Update Details
                                        </Button>
                                    </CardActions>
                                </form>
                            )}
                        </Formik>
                    </Loading>
                </Card>
            </div>
        </AppLayout>
    );
};

export default Profile;
